/**
 * Parâmetros de balanceamento (GDD › Parâmetros de balanceamento).
 * Cada partida pode sobrescrever qualquer valor (`MatchConfig.balance`).
 */
export const DEFAULT_BALANCE = {
  // Combate
  lossExponent: 1.5,
  /** Sorte: cada lado × [1 − luck, 1 + luck]. 0 = determinístico. */
  luck: 0.1,
  defendBonus: 0.25,
  defendSpecialBonus: 0.5,
  marchSpecialBonus: 0.2,
  supportSpecialMultiplier: 1.5,
  fortificationPerLevel: 0.1,
  fortificationMax: 0.5,
  capitalDefenseBonus: 0.2,
  upgradePerLevel: 0.05,
  amphibiousPenalty: 0.3,
  noSupplyPenalty: 0.25,
  noSupplyAttrition: 0.05,
  negativeTreasuryPenalty: 0.1,
  minModifier: 0.25,
  supportLossFactor: 0.5,
  artillerySupportLossFactor: 0.25,
  engineersPerFortLevel: 5,

  // Território
  /** 3 turnos de Consolidar levam um território de 0 a 100 de Controle (★: 2 turnos). */
  consolidateControl: 34,
  consolidateSpecialControl: 50,
  consolidateTaxBonus: 0.5,
  adminControlPerLevel: 2,
  occupationDoctrineBonus: 5,
  unguardedControlLoss: 25,
  occupiedProduction: 0.5,
  revoltBaseChance: 0.5,
  militiaRegen: 0.1,
  militiaAtk: 6,
  militiaDef: 10,
  capitalFallControlLoss: 20,
  capitalFallTreasuryShare: 0.3,

  // Economia
  /** Multiplicador dos impostos sobre o valor fiscal dos territórios. */
  taxMultiplier: 2,
  /** Custo das pesquisas por era (fração da tabela). */
  researchCostFactor: { 1: 0.5, 2: 0.6, 3: 1, 4: 1 },
  /** Bônus de continente: dinheiro por turno por território do continente dominado. */
  continentBonusPerTerritory: 10,
  /** Bônus de continente: matéria-prima e impostos a mais nos territórios do continente dominado. */
  continentBonusYield: 0.1,
  baseStorage: 500,
  warehouseStoragePerLevel: 1000,
  warehouseProtectionPerLevel: 0.1,
  raidMaxShare: 0.5,
  adminTaxPerLevel: 0.1,
  industrialEfficiencyPerLevel: 0.1,
  /** Bolsa: a venda paga (marketSellFactor ÷ marketBuyFactor) do preço de compra do turno. */
  marketSellFactor: 0.5,
  /** Bolsa: multiplicador médio do preço de compra sobre o preço de referência. */
  marketBuyFactor: 2,
  marketPriceMin: 1.2,
  marketPriceMax: 3.5,
  /** Volta à média por turno (fração da distância até marketBuyFactor). */
  marketReversion: 0.25,
  /** Peso de (comprado − vendido) ÷ ofertado no preço do turno seguinte. */
  marketDemandWeight: 0.3,
  /** Variação aleatória do preço por turno (±). */
  marketNoise: 0.1,
  /** Estoque mundial por governo abaixo do qual o recurso é escasso (+ preço) ou acima, farto (− preço). */
  marketScarceStock: 50,
  marketAbundantStock: 1000,
  marketScarcityStep: 0.2,
  marketLotsBase: 6,
  marketLotsPerPlayer: 2,
  marketLotsMax: 30,
  tradeMoneyFee: 0.1,

  // Instalações e pesquisa
  facilityCostGrowth: 1.8,
  facilityTimeGrowth: 1.6,
  productionTimeReductionPerLevel: 0.1,
  researchTimeReductionPerLevel: 0.1,
  maxResearchCenters: 3,
  eraUnlockShare: 0.6,
  techTheftDiscount: 0.25,

  // Ar e detecção
  detectability: { A: 0.5, M: 0.3, B: 0.1 },
  detectionConstant: 20,
  aaMobileCoverage: 3,
  aaBatteryCoveragePerLevel: 5,
  aaBatteryDefPerLevel: 10,
  radarCoveragePerLevel: 10,
  coastalDefenseDefPerLevel: 15,
  missileDefensePerLevel: 0.15,
  missileDefenseMax: 0.75,
  subDetectionChance: 0.2,
  carrierMissions: 3,
  bombingStockFactor: 3,
  /** Mísseis (consumíveis, um uso): estoque destruído = PB × este fator (bombardeiros usam o de cima). */
  missileStockFactor: 6,
  /** Bombardeio de tropas: soldados equivalentes (DEF ÷ 12) mortos = ΣPB ÷ divisor. */
  bombingTroopsDivisor: 2,
  /** Redução do bombardeio de tropas por nível de Fortificações. */
  bombingTroopsFortReduction: 0.1,

  // Nuclear
  nuclearUnitLoss: 0.8,
  nuclearFacilityLevels: 3,
  nuclearStockLoss: 0.8,
  contaminationTurns: 5,
  /** Turnos em que o cogumelo da detonação fica sobre o território no mapa. */
  mushroomCloudTurns: 3,
  contaminationProduction: 0.5,
  nuclearControlLoss: 50,
  asatChance: 0.7,

  // Inteligência
  counterintelPerAgencyLevel: 0.1,
  counterintelPerAdjacentAgencyLevel: 0.05,
  counterintelPerAgent: 0.1,
  counterintelResearchBonus: 0.1,
  counterintelMax: 0.6,
  agentCaptureChance: 0.5,
  /** Agentes por missão: cada um tenta de forma independente. */
  agentsPerMissionMax: 5,
  fomentRevoltControl: 15,
  reconArmoredDetection: 0.3,

  // Logística
  roadCapacity: 200,
  railCapacityPerLevel: 500,
  airCargoCapacity: 100,
  seaCargoCapacity: 200,
  operationsPerLevel: 2,
  seaSpeed: 3,
  railSpeed: 2,
  roadSpeed: 1,

  // Partida
  initialProtectionTurns: 3,
  napBreakNoticeTurns: 2,
  allianceLeaveTurns: 1,
  interimAfterTurns: 3,
  collapseAfterTurns: 10,
  victoryHoldTurns: 3,
  capitalsShare: 0.5,
  controlVictoryShare: 0.7,
  gdpAverageTurns: 5,
} as const;

export type Balance = {
  -readonly [K in keyof typeof DEFAULT_BALANCE]: (typeof DEFAULT_BALANCE)[K] extends object
    ? { -readonly [P in keyof (typeof DEFAULT_BALANCE)[K]]: number }
    : number;
};

export function resolveBalance(overrides?: Partial<Balance>): Balance {
  return {
    ...(DEFAULT_BALANCE as unknown as Balance),
    ...overrides,
    detectability: { ...DEFAULT_BALANCE.detectability, ...overrides?.detectability },
  };
}

/** Kit inicial (GDD › Início de partida). */
export const STARTING_KIT = {
  facilities: { palace: 1, barracks: 1, warehouse: 1, 'research-center': 1 } as Record<string, number>,
  units: { soldier: 40 } as Record<string, number>,
  stock: { food: 300, wood: 300, iron: 200, fuel: 200, steel: 100 },
  treasury: 2000,
} as const;

/** Faixas de tropas estimadas (neblina de guerra). */
export const TROOP_BANDS = [
  { id: 'none', label: 'nenhuma', max: 0 },
  { id: 'small', label: 'pequena', max: 10 },
  { id: 'medium', label: 'média', max: 30 },
  { id: 'large', label: 'grande', max: 80 },
  { id: 'huge', label: 'enorme', max: Number.POSITIVE_INFINITY },
] as const;
export type TroopBand = (typeof TROOP_BANDS)[number]['id'];

export function troopBand(count: number): TroopBand {
  for (const band of TROOP_BANDS) if (count <= band.max) return band.id;
  return 'huge';
}

/** Pontuação de Poder. */
export const SCORE = {
  consolidated: 10,
  startingCapital: 30,
  occupied: 4,
  seaZone: 2,
  gdpPer: 10,
  militaryPer: 100,
  research: 3,
} as const;
